import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useRowPagination } from "hooks/useRowPagination";
import { isFlagEnabled } from "js/utilities";
import { getAlerts } from "service/policy/getAlerts";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import RowPagination from "components/General/RowPagination";
import MessageRow from "components/MessageCenter/MessageRow";
import PolicyFilter from "components/MessageCenter/PolicyFilter";

export default function PolicyAlertsTab({
  selectedPolicyId,
  setSelectedPolicyId,
}) {
  const { t } = useTranslation();
  const { agentPolicies } = useContext(AgentData);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [policyAlerts, setPolicyAlerts] = useState([]);
  const [policyAlertsNotFound, setPolicyAlertsNotFound] = useState(false);
  const { user } = useSelector((state) => state.user);

  const {
    rowsForDisplay: alertMessages,
    rowPaginationProps: alertMessagesProps,
  } = useRowPagination(policyAlerts, [3, 10, 25, "all"]);

  async function onChange(policyId) {
    const selectedPolicy = agentPolicies.find((policy) => {
      if (policyId?.value) {
        return policy?.oldPolicy === policyId?.value;
      }
      return policy?.oldPolicy === policyId;
    });
    if (selectedPolicy && selectedPolicy?.contractID) {
      const AlertsMessage = [];
      setIsLoading(true);
      await getAlerts([selectedPolicy], user)
        .then((response) => {
          setIsLoading(false);
          if (response?.success) {
            response?.gIdpPayLoad?.policyHolder?.alerts?.map((alert) => {
              alert?.messages?.map((message) => {
                AlertsMessage.push({
                  oldPolicy: alert?.memberID,
                  type: "alert",
                  document: {
                    typeOfDocument: "Alerts",
                    description: message?.message,
                  },
                  policy: {
                    contractID: alert?.memberID,
                  },
                });
              });
            });
            setPolicyAlerts(AlertsMessage);
            if (AlertsMessage.length === 0) setPolicyAlertsNotFound(true);
            else setPolicyAlertsNotFound(false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("getAlerts failing", error);
        });
    } else {
      setPolicyAlerts([]);
    }
  }

  useEffect(() => {
    onChange(selectedPolicyId);
  }, [selectedPolicyId]);

  useEffect(() => {
    if (agentPolicies) {
      setIsLoading(true);
      const policySelectionArray = [];
      agentPolicies?.map((policy) => {
        policySelectionArray.push({
          value: policy?.oldPolicy,
          displayAs: `${policy?.oldPolicy}: ${policy?.insuredFullName}`,
          displayAsValue: policy?.oldPolicy,
          ...policy,
        });
      });

      setPolicyFilterOptions(policySelectionArray);
      setIsLoading(false);
    }
  }, [agentPolicies]);

  return (
    <EverlakeLoadingOverlay
      isLoading={isLoading}
      loadingText={t("AlertsCorrespondence-policyalerts-loading-text")}
    >
      {isFlagEnabled("ENABLE_AUTOCOMPLETE_COMBOBOX") ? (
        <div className="grid grid-cols-1 gap-4 my-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          <AgentAutoCompleteComboBox
            labelName="Contract Search"
            inputPlaceholderText={t("AlertsCorrespondence-input-placeholder")}
            options={policyFilterOptions}
            invalidMessage={t("AlertsCorrespondence-input-invalid-msg-1")}
            selectedOption={selectedPolicyId}
            setSelectedOption={setSelectedPolicyId}
            tooltipVerbiage="Search by typing in a keyword below or select an item from the dropdown."
          />
        </div>
      ) : (
        <PolicyFilter
          policyIds={policyFilterOptions.map((option) => option.value)}
          selectedPolicyId={selectedPolicyId}
          setSelectedPolicyId={setSelectedPolicyId}
          currentCategory={"alerts"}
          onchange={onChange}
        />
      )}

      {/* <!-- Display Alerts list --> */}
      {alertMessages.map((message, idx) => (
        <MessageRow
          key={`message - ${message.document.name} - ${idx}`}
          message={message}
          policy={agentPolicies.find((p) => p.oldPolicy === message.oldPolicy)}
        />
      ))}

      {policyAlertsNotFound && (
        <div className="w-full md:ml-6 text-center">
          <h3>{t("AlertsCorrespondence-policyAlerts-text-1")}</h3>
        </div>
      )}
      {/* <!-- paging component --> */}
      {alertMessages.length > 0 && <RowPagination {...alertMessagesProps} />}
    </EverlakeLoadingOverlay>
  );
}
